// frontend/app/components/lessonPlans/authoring/LessonAssetPicker.js
//
// Picks one 3D scene or one video segment from a topic's real catalogue.
//
// Replaces typing raw ids: an author should not have to know a scene's numeric
// PWO id or its internal scene name. The catalogue comes from the Collections
// template_from_topic endpoint, so the names shown here are the same names the
// runtime resolves to.
//
// Inside the lesson wizard (LessonAuthoringContext present) the picker names the
// topic it is listing and can browse any other topic's catalogue, so an empty
// lesson topic is never a dead end. The lesson's own anchor is never changed;
// the runtime resolver falls back to a board-wide search for cross-topic refs.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import TopicAnchorPicker from "./TopicAnchorPicker";
import { useLessonAuthoring } from "./LessonAuthoringContext";
import { apiGetTopicAssetCatalog } from "../../../lessonPlans";

const EMPTY_CATALOG = { pwos: [], videos: [], loading: false, error: null };

/** Fills `{token}` placeholders after translation; unknown tokens stay as written. */
const fill = (text, params) =>
  String(text).replace(/\{(\w+)\}/g, (match, token) =>
    Object.prototype.hasOwnProperty.call(params, token) ? String(params[token]) : match
  );

const matches = (haystack, needle) =>
  String(haystack || "").toLowerCase().includes(needle);

/** Group rows under their parent PWO or video, preserving catalogue order. */
const groupBy = (rows, keyOf, labelOf) => {
  const groups = new Map();
  rows.forEach((row) => {
    const key = String(keyOf(row));
    if (!groups.has(key)) groups.set(key, { key, label: labelOf(row), rows: [] });
    groups.get(key).rows.push(row);
  });
  return [...groups.values()];
};

const LessonAssetPicker = ({
  visible,
  kind, // "pwo" | "video"
  catalog,
  loading = false,
  error = null,
  selectedRef = null,
  onPick,
  onClose,
  isDarkMode = false,
  colors,
  t,
}) => {
  const translate = t || ((key) => key);
  const authoring = useLessonAuthoring();
  const lessonTopic = authoring?.lessonTopic || null;

  const [search, setSearch] = useState("");
  const [browsing, setBrowsing] = useState(false);
  const [browseAnchor, setBrowseAnchor] = useState({});
  const [browseTopic, setBrowseTopic] = useState(null);
  const [browseCatalog, setBrowseCatalog] = useState(EMPTY_CATALOG);
  // Bumped on every topic choice and on close, so a late response for a topic
  // the author has already left is ignored.
  const requestId = useRef(0);

  const resetBrowse = () => {
    requestId.current += 1;
    setSearch("");
    setBrowsing(false);
    setBrowseAnchor({});
    setBrowseTopic(null);
    setBrowseCatalog(EMPTY_CATALOG);
  };

  // Parents only toggle `visible`, so reset our state whenever the picker closes.
  useEffect(() => {
    if (!visible) resetBrowse();
  }, [visible]);

  const startBrowse = () => {
    setSearch("");
    setBrowseAnchor({
      level_id: lessonTopic?.level_id,
      subject_id: lessonTopic?.subject_id,
      chapter_id: lessonTopic?.chapter_id,
    });
    setBrowsing(true);
  };

  const chooseTopic = async (anchor) => {
    setBrowseAnchor(anchor);
    if (!anchor.board_topic_id) {
      // Grade/subject/chapter changed after a topic was chosen: drop that
      // topic's list and ignore any response still on its way.
      requestId.current += 1;
      setBrowseTopic(null);
      setBrowseCatalog(EMPTY_CATALOG);
      return;
    }

    const id = ++requestId.current;
    setBrowseTopic({ board_topic_id: anchor.board_topic_id, topic_name: anchor.topic_name });
    setBrowseCatalog({ ...EMPTY_CATALOG, loading: true });
    try {
      const next = await apiGetTopicAssetCatalog(authoring.fetchWithAuth, {
        board: authoring.board,
        boardTopicId: anchor.board_topic_id,
        sourceTopicId: anchor.source_topic_id ?? anchor.board_topic_id,
      });
      if (id !== requestId.current) return;
      if (authoring.rememberRows) authoring.rememberRows(next);
      setBrowseCatalog({ ...EMPTY_CATALOG, ...next, loading: false, error: null });
    } catch (error) {
      if (id !== requestId.current) return;
      setBrowseCatalog({
        ...EMPTY_CATALOG,
        error: error?.message || translate("Could not load this topic's content"),
      });
    }
  };

  const isPwo = kind === "pwo";
  // The catalogue actually on screen: the lesson's, or the browsed topic's.
  const listed = browsing ? (browseTopic ? browseCatalog : EMPTY_CATALOG) : catalog;
  const listLoading = browsing ? browseCatalog.loading : loading;
  const listError = browsing ? browseCatalog.error : error;
  const rows = (isPwo ? listed?.pwos : listed?.videos) || [];
  const sourceName = browsing ? browseTopic?.topic_name : lessonTopic?.topic_name;

  const groups = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = needle
      ? rows.filter((row) =>
          isPwo
            ? matches(row.scene_label, needle) || matches(row.pwo_label, needle)
            : matches(row.tag_label, needle) || matches(row.video_label, needle)
        )
      : rows;

    return isPwo
      ? groupBy(filtered, (r) => r.source_pwo_id, (r) => r.pwo_label)
      : groupBy(filtered, (r) => r.source_video_id, (r) => r.video_label);
  }, [rows, search, isPwo]);

  const isSelected = (row) =>
    isPwo
      ? selectedRef?.source_pwo_id === row.source_pwo_id &&
        selectedRef?.scene_name === row.scene_name
      : selectedRef?.source_video_id === row.source_video_id &&
        (selectedRef?.tag_id ?? null) === (row.tag_id ?? null);

  const emptyMessage = sourceName
    ? fill(
        translate(isPwo ? "{topic} has no 3D scenes." : "{topic} has no video segments."),
        { topic: sourceName }
      )
    : translate("This topic has no content of that kind. Check the lesson's topic on step 1.");

  const pick = (row) => {
    onPick(
      isPwo
        ? { source_pwo_id: row.source_pwo_id, scene_name: row.scene_name }
        : {
            source_video_id: row.source_video_id,
            tag_id: row.tag_id ?? null,
            is_full_video: row.tag_id === null || row.tag_id === undefined,
          }
    );
    onClose();
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View
          style={[
            styles.sheet,
            authoring ? styles.sheetWide : null,
            { backgroundColor: colors.panelBg, borderColor: colors.border },
          ]}
        >
          <View style={[styles.header, { borderBottomColor: colors.border }]}>
            <MaterialCommunityIcons
              name={isPwo ? "cube-outline" : "play-circle-outline"}
              size={18}
              color={colors.accentPrimary}
            />
            <Text style={[styles.title, { color: colors.textPrimary }]}>
              {isPwo ? translate("Choose a 3D scene") : translate("Choose a video segment")}
            </Text>
            <View style={{ flex: 1 }} />
            <TouchableOpacity
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel={translate("Close")}
              style={[styles.close, { borderColor: colors.border }]}
            >
              <MaterialCommunityIcons name="close" size={16} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>

          <View style={styles.searchRow}>
            <TextInput
              value={search}
              onChangeText={setSearch}
              placeholder={translate("Search…")}
              placeholderTextColor={colors.textMuted}
              accessibilityLabel={translate("Search")}
              style={[
                styles.search,
                {
                  backgroundColor: colors.subtleBg,
                  borderColor: colors.border,
                  color: colors.textPrimary,
                },
              ]}
            />
            {authoring ? (
              <TouchableOpacity
                onPress={browsing ? resetBrowse : startBrowse}
                accessibilityRole="button"
                style={styles.browseLink}
              >
                <Text style={[styles.browseLinkText, { color: colors.accentPrimary }]}>
                  {browsing
                    ? translate("Back to this lesson's topic")
                    : translate("Browse another topic")}
                </Text>
              </TouchableOpacity>
            ) : null}
            {sourceName ? (
              <Text style={[styles.source, { color: colors.textMuted }]}>
                {fill(translate("From: {topic}"), { topic: sourceName })}
              </Text>
            ) : null}
          </View>

          <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
            {browsing ? (
              <View style={styles.browser}>
                <TopicAnchorPicker
                  colors={colors}
                  curriculum={authoring.curriculum}
                  value={browseAnchor}
                  onChange={chooseTopic}
                  depth="topic"
                  t={t}
                />
              </View>
            ) : null}

            {browsing && !browseTopic ? null : listLoading ? (
              <View style={styles.state}>
                <ActivityIndicator size="small" color={colors.accentPrimary} />
                <Text style={[styles.stateText, { color: colors.textMuted }]}>
                  {translate("Loading this topic's content…")}
                </Text>
              </View>
            ) : listError ? (
              <View style={styles.state}>
                <MaterialCommunityIcons
                  name="alert-outline"
                  size={18}
                  color={colors.danger}
                />
                <Text style={[styles.stateText, { color: colors.textSecondary }]}>
                  {listError}
                </Text>
              </View>
            ) : groups.length === 0 ? (
              <View style={styles.state}>
                <Text style={[styles.stateText, { color: colors.textMuted }]}>
                  {rows.length === 0
                    ? emptyMessage
                    : translate("Nothing matches that search.")}
                </Text>
                {rows.length === 0 && authoring && !browsing ? (
                  <TouchableOpacity
                    onPress={startBrowse}
                    accessibilityRole="button"
                    style={[styles.browseButton, { borderColor: colors.accentPrimary }]}
                  >
                    <Text style={[styles.browseLinkText, { color: colors.accentPrimary }]}>
                      {translate("Browse another topic")}
                    </Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            ) : (
              groups.map((group) => (
                <View key={group.key} style={styles.group}>
                  <Text style={[styles.groupLabel, { color: colors.textMuted }]}>
                    {group.label}
                  </Text>

                  {group.rows.map((row) => {
                    const selected = isSelected(row);
                    const label = isPwo ? row.scene_label : row.tag_label;
                    const meta = isPwo
                      ? `${translate("scene")} ${row.scene_name}`
                      : row.tag_start_time
                        ? `${translate("starts at")} ${row.tag_start_time}`
                        : translate("whole video");

                    return (
                      <TouchableOpacity
                        key={`${group.key}-${isPwo ? row.scene_name : row.tag_id}`}
                        onPress={() => pick(row)}
                        accessibilityRole="button"
                        accessibilityState={{ selected }}
                        accessibilityLabel={label}
                        style={[
                          styles.row,
                          {
                            backgroundColor: selected ? colors.accentGlow : colors.subtleBg,
                            borderColor: selected ? colors.accentPrimary : colors.border,
                          },
                        ]}
                      >
                        <View style={{ flex: 1, minWidth: 0 }}>
                          <Text
                            style={[styles.rowLabel, { color: colors.textPrimary }]}
                            numberOfLines={1}
                          >
                            {label}
                          </Text>
                          <Text style={[styles.rowMeta, { color: colors.textMuted }]}>
                            {meta}
                          </Text>
                        </View>
                        {selected ? (
                          <MaterialCommunityIcons
                            name="check-circle"
                            size={17}
                            color={colors.accentPrimary}
                          />
                        ) : null}
                      </TouchableOpacity>
                    );
                  })}
                </View>
              ))
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(2,6,23,0.55)",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  sheet: {
    width: "100%",
    maxWidth: 620,
    maxHeight: "88%",
    borderRadius: 16,
    borderWidth: 1,
    overflow: "hidden",
  },
  // Wide enough for TopicAnchorPicker's four columns.
  sheetWide: { maxWidth: 760 },
  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: 9,
    paddingHorizontal: 16,
    paddingVertical: 13,
    borderBottomWidth: 1,
  },
  title: { fontSize: 15, fontWeight: "800" },
  close: {
    width: 28,
    height: 28,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },

  searchRow: { paddingHorizontal: 16, paddingTop: 12 },
  search: {
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 9,
    fontSize: 13,
  },

  browseLink: { alignSelf: "flex-start", paddingTop: 8 },
  browseLinkText: { fontSize: 12, fontWeight: "700", textDecorationLine: "underline" },
  browseButton: {
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginTop: 4,
  },
  source: { fontSize: 11, marginTop: 6 },
  browser: { marginBottom: 14 },

  list: { flexGrow: 0 },
  listContent: { padding: 16, paddingTop: 12 },
  state: { alignItems: "center", paddingVertical: 28, gap: 8 },
  stateText: { fontSize: 13, textAlign: "center", paddingHorizontal: 16 },

  group: { marginBottom: 16 },
  groupLabel: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.6,
    textTransform: "uppercase",
    marginBottom: 7,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 6,
    gap: 8,
  },
  rowLabel: { fontSize: 13, fontWeight: "600" },
  rowMeta: { fontSize: 11, marginTop: 2 },
});

export default LessonAssetPicker;
